<template>
  <div>
    <div class="aides-header">
      <strong>Aides de minimis perçues les 3 dernières années</strong>
      <v-chip x-small outlined class="ml-2">
        {{ value && value.length ? value.length : 0 }} ligne{{ value && value.length > 1 ? "s" : "" }}
      </v-chip>
    </div>

    <table v-if="value && value.length" class="aides-table">
      <thead>
        <tr>
          <th style="width: 45%">Date d'attribution (JJ/MM/AAAA)</th>
          <th style="width: 45%">Montant € (HT)</th>
          <th style="width: 10%"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(aide, idx) in value" :key="idx">
          <td>
            <v-menu
              v-model="menus[idx]"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :value="aide.dateAttribution"
                  dense
                  outlined
                  hide-details="auto"
                  maxlength="10"
                  placeholder="01/03/2024"
                  prepend-inner-icon="mdi-calendar"
                  :rules="[rules.requiredDate, rules.dateFr]"
                  v-bind="attrs"
                  v-on="on"
                  @input="(v) => updateAide(idx, 'dateAttribution', v)"
                ></v-text-field>
              </template>
              <v-date-picker
                :value="frToIso(aide.dateAttribution)"
                locale="fr-FR"
                first-day-of-week="1"
                @input="(iso) => onPickerInput(idx, iso)"
              ></v-date-picker>
            </v-menu>
          </td>
          <td>
            <v-text-field
              :value="aide.montantAides"
              dense
              outlined
              hide-details="auto"
              type="number"
              step="0.01"
              min="0"
              suffix="€"
              :rules="[rules.requiredMontant, rules.positive]"
              @input="(v) => updateAide(idx, 'montantAides', Number(v))"
            ></v-text-field>
          </td>
          <td class="aide-actions">
            <v-btn icon color="red" @click="removeAide(idx)" title="Supprimer cette ligne">
              <v-icon>mdi-delete-outline</v-icon>
            </v-btn>
          </td>
        </tr>
      </tbody>
    </table>

    <div v-else class="aides-empty">Aucune aide saisie.</div>

    <v-btn small outlined color="#e2007a" class="mt-2" @click="addAide">
      <v-icon small left>mdi-plus</v-icon>
      Ajouter une aide
    </v-btn>
  </div>
</template>

<script>
// Tableau dynamique des aides de minimis pour l'édition admin.
// v-model = array d'objets { dateAttribution: 'JJ/MM/AAAA', montantAides: number }.
// Émet 'input' à chaque modification ; le parent reconstruit le payload diff.

const DATE_FR_RE = /^\d{2}\/\d{2}\/\d{4}$/;
const DATE_ISO_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export default {
  name: "AidesDeMinimisEditor",

  props: {
    value: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      // Etat ouvert/ferme du calendrier par index de ligne. Recree a chaque
      // changement de longueur via watch pour eviter les fuites d'index.
      menus: [],
      rules: {
        requiredDate: (v) => (!!v && v.length > 0) || "Date requise",
        dateFr: (v) => DATE_FR_RE.test(String(v || "")) || "Format JJ/MM/AAAA",
        requiredMontant: (v) =>
          (v !== null && v !== undefined && v !== "") || "Montant requis",
        positive: (v) => Number(v) > 0 || "Montant > 0 €",
      },
    };
  },

  watch: {
    value: {
      immediate: true,
      handler(arr) {
        const len = Array.isArray(arr) ? arr.length : 0;
        if (this.menus.length !== len) {
          this.menus = new Array(len).fill(false);
        }
      },
    },
  },

  methods: {
    addAide() {
      const next = [...(this.value || []), { dateAttribution: "", montantAides: 0 }];
      this.$emit("input", next);
    },

    removeAide(idx) {
      const next = (this.value || []).filter((_, i) => i !== idx);
      this.$emit("input", next);
    },

    updateAide(idx, field, val) {
      const next = (this.value || []).map((aide, i) =>
        i === idx ? { ...aide, [field]: val } : aide
      );
      this.$emit("input", next);
    },

    // Selection dans le mini-calendar : conversion ISO -> JJ/MM/AAAA et fermeture.
    onPickerInput(idx, iso) {
      const fr = this.isoToFr(iso);
      this.updateAide(idx, "dateAttribution", fr);
      this.$set(this.menus, idx, false);
    },

    // JJ/MM/AAAA -> YYYY-MM-DD pour bind du v-date-picker. Renvoie null si vide
    // ou format invalide (le picker s'ouvre alors sur le mois courant).
    frToIso(dateFr) {
      if (!dateFr || !DATE_FR_RE.test(dateFr)) return null;
      const [d, m, y] = dateFr.split("/");
      return `${y}-${m}-${d}`;
    },

    // YYYY-MM-DD -> JJ/MM/AAAA pour affichage et stockage v-model.
    isoToFr(iso) {
      if (!iso) return "";
      const m = DATE_ISO_RE.exec(iso);
      return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
    },
  },
};
</script>

<style scoped>
.aides-header {
  display: flex;
  align-items: center;
  margin-bottom: 8px;
  font-size: 13px;
}
.aides-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}
.aides-table th {
  text-align: left;
  padding: 4px 8px;
  font-weight: 600;
  color: #555;
}
.aides-table td {
  padding: 4px 8px;
  vertical-align: top;
}
/* Poubelle alignee verticalement au centre des inputs et collee a droite */
.aides-table td.aide-actions {
  vertical-align: middle;
  text-align: right;
  padding-right: 0;
}
.aides-empty {
  font-size: 12px;
  color: #888;
  font-style: italic;
  padding: 8px 0;
}
</style>
